import { useMemo } from 'react';
import { FormCheckbox, SegmentedControl } from '@/components/UI';
import { heatmapStyle } from '@/utils/pnlHeatmap';
import type { PerformanceDay, PerformanceTrade } from '../performanceTypes';
import { formatSignedCurrency, formatSignedPercent, signedToneClass } from '../performanceFormat';
import {
    buildCalendarMonth,
    buildYearStrip,
    shiftMonthKey,
    type CalendarCell,
    type CalendarMode,
    type PerformanceBasis,
    type StrategyScope
} from '../performanceUtils';
import StrategyColorDot from './StrategyColorDot';

interface PnlCalendarProps {
    days: PerformanceDay[];
    dayByDate: Map<string, PerformanceDay>;
    trades: PerformanceTrade[];
    monthKey: string;
    onMonthChange: (monthKey: string) => void;
    mode: CalendarMode;
    onModeChange: (mode: CalendarMode) => void;
    basis: PerformanceBasis;
    scope: StrategyScope;
    strategyColors: Map<number, string>;
    today: string;
    hideWeekends: boolean;
    onHideWeekendsChange: (hide: boolean) => void;
    selectedDate: string | null;
    onSelectDay: (date: string) => void;
}

const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const cellClasses = (cell: CalendarCell, selected: boolean) => {
    const base =
        'relative flex min-h-[68px] w-full min-w-0 flex-col items-start gap-0.5 rounded-md border p-1.5 text-left transition-colors';
    const border = cell.isToday ? 'border-cyan-600' : 'border-zinc-800';
    const interactive = cell.hasData ? 'hover:border-zinc-500 cursor-pointer' : 'cursor-default opacity-50';
    const ring = selected ? 'outline outline-2 outline-offset-[-2px] outline-zinc-200' : '';
    return `${base} ${border} ${interactive} ${ring} bg-[#121212]`;
};

/**
 * Month calendar of daily P&L with weekly totals, plus a twelve-month strip for jumping around the year.
 * Cell color intensity scales to the largest move in the visible month.
 */
const PnlCalendar = ({
    days,
    dayByDate,
    trades,
    monthKey,
    onMonthChange,
    mode,
    onModeChange,
    basis,
    scope,
    strategyColors,
    today,
    hideWeekends,
    onHideWeekendsChange,
    selectedDate,
    onSelectDay
}: PnlCalendarProps) => {
    const year = Number(monthKey.slice(0, 4));

    const month = useMemo(
        () => buildCalendarMonth({ dayByDate, trades, monthKey, mode, basis, scope, today }),
        [dayByDate, trades, monthKey, mode, basis, scope, today]
    );

    const yearStrip = useMemo(() => buildYearStrip({ days, year, mode, basis, scope }), [days, year, mode, basis, scope]);

    const firstDataMonth = days[0]?.date.slice(0, 7) ?? monthKey;
    const lastMonth = today.slice(0, 7);
    const visibleColumns = hideWeekends ? 5 : 7;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <button
                        aria-label="Previous year"
                        className="rounded-md border border-zinc-700 px-2 py-1 text-sm text-zinc-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                        disabled={`${year}-12` < firstDataMonth}
                        type="button"
                        onClick={() => onMonthChange(shiftMonthKey(monthKey, -12))}
                    >
                        ‹ {year - 1}
                    </button>
                    <span className="text-sm font-semibold text-white">{year}</span>
                    <button
                        aria-label="Next year"
                        className="rounded-md border border-zinc-700 px-2 py-1 text-sm text-zinc-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                        disabled={`${year + 1}-01` > lastMonth}
                        type="button"
                        onClick={() => onMonthChange(shiftMonthKey(monthKey, 12))}
                    >
                        {year + 1} ›
                    </button>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <SegmentedControl
                        ariaLabel="Calendar value"
                        options={[
                            {
                                value: 'equity',
                                label: 'Equity change',
                                title: 'Mark-to-market change from the first to the last snapshot of the day'
                            },
                            { value: 'realized', label: 'Realized closes', title: 'P&L of trades closed that day' }
                        ]}
                        value={mode}
                        onChange={onModeChange}
                    />
                    <FormCheckbox
                        checked={hideWeekends}
                        label="Hide weekends"
                        onChange={(event) => onHideWeekendsChange(event.target.checked)}
                    />
                </div>
            </div>

            <div className="grid grid-cols-6 gap-1 md:grid-cols-12">
                {yearStrip.map((entry) => (
                    <button
                        aria-pressed={entry.monthKey === monthKey}
                        className={`min-w-0 rounded-md border px-1 py-1.5 text-center text-[11px] transition-colors ${
                            entry.monthKey === monthKey ? 'border-zinc-200' : 'border-zinc-800 hover:border-zinc-600'
                        } ${entry.hasData ? '' : 'opacity-40'}`}
                        disabled={!entry.hasData}
                        key={entry.monthKey}
                        type="button"
                        onClick={() => onMonthChange(entry.monthKey)}
                    >
                        <div className="text-zinc-400">{entry.label}</div>
                        <div
                            className={`truncate text-xs font-semibold ${entry.hasData ? signedToneClass(entry.total, 'text-zinc-300') : 'text-zinc-600'}`}
                        >
                            {entry.hasData ? formatSignedCurrency(entry.total, 0) : '—'}
                        </div>
                        {entry.returnPercent !== null && (
                            <div className={`text-[10px] ${signedToneClass(entry.returnPercent)}`}>
                                {formatSignedPercent(entry.returnPercent, 1)}
                            </div>
                        )}
                    </button>
                ))}
            </div>

            <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-baseline gap-2">
                        <span className="text-lg font-semibold text-white">{month.label}</span>
                        <span className="text-sm text-zinc-400">
                            <b className={signedToneClass(month.total, 'text-zinc-300')}>{formatSignedCurrency(month.total, 0)}</b>
                            {month.returnPercent !== null && (
                                <span className={`ml-1 ${signedToneClass(month.returnPercent)}`}>
                                    {formatSignedPercent(month.returnPercent)}
                                </span>
                            )}
                            {' · '}
                            {month.activeDays} active day{month.activeDays === 1 ? '' : 's'} ·{' '}
                            {mode === 'equity' ? 'equity change' : 'realized closes'}
                        </span>
                    </div>
                    <div className="flex gap-1">
                        <button
                            aria-label="Previous month"
                            className="h-8 w-8 rounded-md border border-zinc-700 text-zinc-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                            disabled={monthKey <= firstDataMonth}
                            type="button"
                            onClick={() => onMonthChange(shiftMonthKey(monthKey, -1))}
                        >
                            ‹
                        </button>
                        <button
                            aria-label="Next month"
                            className="h-8 w-8 rounded-md border border-zinc-700 text-zinc-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                            disabled={monthKey >= lastMonth}
                            type="button"
                            onClick={() => onMonthChange(shiftMonthKey(monthKey, 1))}
                        >
                            ›
                        </button>
                    </div>
                </div>

                <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${visibleColumns}, minmax(0, 1fr)) minmax(72px, 96px)` }}>
                    {WEEKDAY_HEADERS.slice(0, visibleColumns).map((label) => (
                        <div className="px-1 pb-1 text-[11px] uppercase tracking-wider text-zinc-500" key={label}>
                            {label}
                        </div>
                    ))}
                    <div className="px-1 pb-1 text-right text-[11px] uppercase tracking-wider text-zinc-500">Week</div>

                    {month.weeks.map((week) => (
                        <div className="contents" key={week.weekStartDate}>
                            {week.cells.slice(0, visibleColumns).map((cell, index) =>
                                cell === null ? (
                                    <div key={`${week.weekStartDate}-blank-${index}`} />
                                ) : (
                                    <button
                                        aria-label={`${cell.date}: ${cell.hasData ? formatSignedCurrency(cell.value) : 'no data'}`}
                                        className={cellClasses(cell, selectedDate === cell.date)}
                                        disabled={!cell.hasData}
                                        key={cell.date}
                                        style={cell.hasData && !cell.isFlat ? heatmapStyle(cell.value, month.scale) : undefined}
                                        title={cell.hasData ? `${cell.date} · ${formatSignedCurrency(cell.value)}` : cell.date}
                                        type="button"
                                        onClick={() => onSelectDay(cell.date)}
                                    >
                                        <span className="text-[11px] text-zinc-500">{cell.dayOfMonth}</span>
                                        {cell.hasData && (
                                            <span
                                                className={`w-full truncate text-[13px] font-semibold ${cell.isFlat ? 'text-zinc-600' : signedToneClass(cell.value, 'text-zinc-300')}`}
                                            >
                                                {cell.isFlat ? '—' : formatSignedCurrency(cell.value, 0)}
                                            </span>
                                        )}
                                        {(cell.closedCount > 0 || cell.openedCount > 0 || cell.hasFunding) && (
                                            <span className="flex w-full items-center gap-1 overflow-hidden whitespace-nowrap text-[10px] text-zinc-400">
                                                {cell.closedStrategyIds.slice(0, 6).map((id) => (
                                                    <StrategyColorDot color={strategyColors.get(id) ?? '#71717a'} key={id} size="sm" />
                                                ))}
                                                {[
                                                    cell.closedCount ? `${cell.closedCount} closed` : null,
                                                    cell.openedCount ? `${cell.openedCount} opened` : null,
                                                    cell.hasFunding ? 'funding' : null
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </span>
                                        )}
                                    </button>
                                )
                            )}
                            <div className="flex min-h-[68px] flex-col items-end justify-center rounded-md border border-zinc-800 bg-[#151515] px-2 py-1 text-xs">
                                <b className={`text-[13px] ${signedToneClass(week.total, 'text-zinc-500')}`}>
                                    {week.total === 0 ? '—' : formatSignedCurrency(week.total, 0)}
                                </b>
                                {week.closedCount > 0 && <span className="text-[10px] text-zinc-500">{week.closedCount} closed</span>}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-2 text-xs text-zinc-500">
                    Color intensity scales to the largest move in the month. Click a day for its breakdown. Dots show the strategies of
                    trades closed that day.
                </div>
            </div>
        </div>
    );
};

export default PnlCalendar;
